/**
 * Pure validation for the renew-decision form (design §5 Renew lifecycle + §13 grace_days rule) — kept
 * separate from the component so the validation STATES (the thing the quality gate explicitly asks for
 * tests on) can be unit-tested without mounting the modal.
 */
import { dayAfter } from "@/features/pool/dateUtils";
import type { PoolCreatePeriodBody, PoolExpiryAction } from "@/api/types";
import type { TKey } from "@/i18n/keys";

export type RenewChoice = "undecided" | "yes" | "no";

export function renewChoiceFromFlag(renew: boolean | null): RenewChoice {
  if (renew === true) return "yes";
  if (renew === false) return "no";
  return "undecided";
}

/** grace_days is only ever shown/required for the grace_2stage action — the DDL's own CHECK constraint
 *  (`grace_days_iff_grace`) mirrors this exactly, so the FE gate agrees with what the server enforces. */
export function graceDaysVisible(action: PoolExpiryAction): boolean {
  return action === "grace_2stage";
}

/** Validates the "No" (renew=false) branch: an expiry action is mandatory, and grace_2stage additionally
 *  requires a positive integer grace_days. Returns a translation key for the first violation, or null. */
export function validateRenewNo(
  action: PoolExpiryAction | null,
  graceDaysRaw: string,
): TKey | null {
  if (!action) return "pool.period.validation.expiryActionRequired";
  if (!graceDaysVisible(action)) return null;
  const trimmed = graceDaysRaw.trim();
  if (!trimmed) return "pool.period.validation.graceDaysRequired";
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return "pool.period.validation.graceDaysPositive";
  return null;
}

/**
 * Both dates present and end >= start — the server's `sane_range` CHECK, mirrored client-side. Split
 * out of validateRenewYes on 30/7: BulkSetPeriodDialog was calling that function for THIS job (its own
 * period range, nothing to do with renewing), so when Yes changed shape the old name would have kept
 * compiling while quietly checking the wrong thing.
 */
export function validateRange(start: string, end: string): TKey | null {
  if (!start || !end) return "pool.period.validation.endAfterStart";
  if (end < start) return "pool.period.validation.endAfterStart";
  return null;
}

/**
 * Validates the "Yes" (renew=true) branch (30/7, คุณใหม่ bug 1). ONE date is entered — the next round's
 * END. Its START is derived (`dayAfter(periodEnd)`, dateUtils) because the rule is "ต่อกันทันที": the
 * operator never picks it, so there is nothing to validate about it, only about whether their end date
 * lands after it. Mirrors periods.validate_renew_decision + pool_0009's `next_period_sane_range`.
 *
 * Was `validateRenewYes(newStart, newEnd)` for the old renew-NOW flow (two free dates, both required).
 * That flow still exists as the early-renewal endpoint, but it is no longer what "Yes" means.
 */
export function validateRenewYes(periodEnd: string, nextEnd: string): TKey | null {
  if (!nextEnd) return "pool.period.validation.nextEndRequired";
  if (nextEnd < dayAfter(periodEnd)) return "pool.period.validation.nextEndAfterStart";
  return null;
}

/**
 * The POST /pool/periods request body, built ONCE here so PeriodModal.submitCreate and
 * BulkSetPeriodDialog's per-email run loop cannot drift on the payload shape (a hand-copied second
 * copy is exactly the drift feedback_union_from_array_not_handwritten.md warns about). Byte-for-byte the
 * object PeriodModal.submitCreate used to build inline: `renew` maps yes/no/undecided → true/false/null,
 * and ONLY the "no" branch carries `expiry_action` (+ `grace_days`, and only for grace_2stage). Matches
 * PoolCreatePeriodBody (api/types.ts) and the server's own renew=false validation.
 *
 * 30/7 — the "yes" branch now carries `next_period_end` (and ONLY that branch: the server 422s a
 * next_period_end sent alongside renew != true rather than accepting and dropping it). The start is
 * never sent — see validateRenewYes above.
 */
export function buildCreatePeriodBody(
  email: string,
  f: {
    start: string;
    end: string;
    renewChoice: RenewChoice;
    expiryAction: PoolExpiryAction;
    graceDays: string;
    nextEnd?: string;
  },
): PoolCreatePeriodBody {
  return {
    email,
    period_start: f.start,
    period_end: f.end,
    renew: f.renewChoice === "yes" ? true : f.renewChoice === "no" ? false : null,
    ...(f.renewChoice === "no"
      ? {
          expiry_action: f.expiryAction,
          ...(graceDaysVisible(f.expiryAction) ? { grace_days: Number(f.graceDays) } : {}),
        }
      : {}),
    ...(f.renewChoice === "yes" && f.nextEnd ? { next_period_end: f.nextEnd } : {}),
  };
}
